import { XMarkIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import {
  useAdForgeGetBrandKitQuery,
  type OverlayDoc,
} from "~/ui-rtk/api/adForgeApi"
import { useGetAssetQuery } from "~/ui-rtk/api/assetsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import type { ScrapedImage } from "../../types"
import { rescaleDoc } from "./BrandOverlayPanel"
import OverlayCanvasEditor from "./OverlayCanvasEditor"
import { useCustomBrandFonts } from "./useCustomBrandFonts"
import { dimsForRatioLabel } from "./overlay-baking"

/**
 * Per-placement arranging: edit the card's layout AT one placement's frame
 * (Feed 1.91:1, Stories 9:16, …) and store the result as that slot's
 * override. Slots without an override keep auto re-solving the card's base
 * layout; a slot arranged here uses its own doc in previews AND at bake.
 */

export type PlacementSlotKey = "landscape" | "portrait" | "stories" | "square"

export interface PlacementOverlayTarget {
  card: ScrapedImage
  slot: PlacementSlotKey
  slotLabel: string
  ratio: string
  media: { type: "image" | "video"; src: string } | null
}

interface PlacementOverlayEditorModalProps {
  target: PlacementOverlayTarget | null
  onClose: () => void
  onSave: (slot: PlacementSlotKey, doc: OverlayDoc) => void
  onReset: (slot: PlacementSlotKey) => void
}

export default function PlacementOverlayEditorModal({
  target,
  onClose,
  onSave,
  onReset,
}: PlacementOverlayEditorModalProps) {
  const open = target !== null
  useCustomBrandFonts()
  const { data: brandKit } = useAdForgeGetBrandKitQuery(undefined, {
    skip: !open,
  })
  const { data: logoAsset } = useGetAssetQuery(brandKit?.logoAssetId ?? "", {
    skip: !open || !brandKit?.logoAssetId,
  })
  const logoUrl = logoAsset?.url || logoAsset?.thumbnailUrl || null

  const [doc, setDoc] = useState<OverlayDoc | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Seed the working doc when a tile's edit button opens the modal: the
  // slot's existing override, or the base layout re-solved at this ratio.
  useEffect(() => {
    if (!target) {
      setDoc(null)
      setSelectedId(null)
      return
    }
    const base = target.card.overlayDoc
    const override = target.card.overlayDocByPlacement?.[target.slot]
    setDoc(
      override ??
        (base ? rescaleDoc(base, dimsForRatioLabel(target.ratio)) : null),
    )
    setSelectedId(null)
  }, [target])

  const hasOverride = !!target?.card.overlayDocByPlacement?.[target.slot]

  return (
    <Dialog
      isOpen={open}
      handleClose={onClose}
      width={640}
      closeOnClickOutside={false}
      classes={{
        container:
          "rounded-lg bg-system-blue-800 text-left text-white shadow-xl",
      }}
    >
      <div className="flex items-center justify-between border-b border-system-blue-700 px-system-lg py-system-md">
        <div>
          <h2 className="text-lg font-semibold">
            Arrange layout — {target?.slotLabel}
          </h2>
          <p className="text-system-gray-300 text-xs">
            {target?.ratio} frame. This placement keeps its own arrangement;
            others still follow the card&apos;s layout.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-system-gray-300 hover:text-white"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-system-lg py-system-md">
        {doc && target ? (
          <OverlayCanvasEditor
            doc={doc}
            onChange={setDoc}
            media={target.media}
            logoUrl={logoUrl}
            brandKit={brandKit}
            selectedId={selectedId}
            onSelectId={setSelectedId}
          />
        ) : (
          <div className="py-system-lg text-center text-system-gray-300 text-sm">
            This card has no layout applied yet.
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-system-sm border-t border-system-blue-700 px-system-lg py-system-md">
        {hasOverride && target && (
          <button
            type="button"
            onClick={() => {
              onReset(target.slot)
              onClose()
            }}
            className="mr-auto rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm text-white hover:bg-system-blue-600"
            title="Drop this placement's arrangement and follow the card's layout again"
          >
            Reset to card layout
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-system-gray-200 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (doc && target) {
              onSave(target.slot, doc)
              onClose()
            }
          }}
          disabled={!doc}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          Save arrangement
        </button>
      </div>
    </Dialog>
  )
}
